"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import Script from "next/script";
import { usePathname } from "next/navigation";
import { GoogleAnalytics } from "@next/third-parties/google";
import { clickTargetPath, resolveClickTarget } from "@/components/analytics/clickTarget";
import { NavigationObserver } from "@/components/analytics/NavigationObserver";
import { onceTitleIsSet } from "@/components/analytics/onceTitleIsSet";
import { isPageContextEnabled } from "@/components/analytics/pageContext";
import { useConsent } from "./ConsentProvider";
import { buildSalesforceConsents } from "./ConsentProvider_helpers";
import { clickEngagementEventName, initSalesforceInteractions, isSalesforceInteractionsReady, sendWebsiteEngagementEvent } from "./salesforceInteractions";
import { useVisitorToken } from "./useVisitorToken";
import { useZiCompanyBridge } from "./ziCompany";

/**
 * ThirdPartyScripts
 *
 * Renders GTM, GA4, and the Salesforce Data Cloud c360a beacon only after the
 * visitor has granted the relevant consent. On a cold load these elements are
 * absent from the DOM, so Chrome/Lighthouse observe zero network requests to
 * the vendor origins and no third-party cookies are written — the behaviour
 * the Best Practices `third-party-cookies`, `deprecations`, and
 * `inspector-issues` audits measure.
 *
 * The c360a beacon's bundled sitemap drives everything (its own consent
 * banner, SDK init, page-view tracking) from DOMContentLoaded, which has long
 * fired by the time a client-injected script runs. So once the script loads we
 * initialize the SDK with the visitor's consent ourselves and emit the same
 * website-engagement events the sitemap template would have, including on
 * client-side navigations the template never handled.
 */
export function ThirdPartyScripts() {
  const $ = _c(26);
  useVisitorToken();
  const {
    marketingConsent,
    analyticsConsent
  } = useConsent();
  const pathname = usePathname();
  const gtmId = process.env.NEXT_PUBLIC_GTM_ID;
  const gaId = process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID;
  const c360aBeaconId = process.env.NEXT_PUBLIC_SALESFORCE_C360A_BEACON_ID;
  const marketingGranted = marketingConsent === "granted";
  const analyticsGranted = analyticsConsent === "granted";
  const anyGranted = marketingGranted || analyticsGranted;
  const ziCompany = useZiCompanyBridge(marketingGranted && Boolean(c360aBeaconId));
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = isPageContextEnabled();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const pageContextEnabled = t0;
  const cancelPendingPageView = useRef(null);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      cancelPendingPageView.current?.();
      let cancelTitleWait;
      const tick = setTimeout(() => {
        cancelTitleWait = onceTitleIsSet(() => {
          cancelPendingPageView.current = null;
          sendWebsiteEngagementEvent("page-view");
        });
      }, 0);
      cancelPendingPageView.current = () => {
        clearTimeout(tick);
        cancelTitleWait?.();
      };
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const sendPageViewSoon = t1;
  let t2;
  if ($[2] !== analyticsGranted || $[3] !== marketingGranted || $[4] !== ziCompany) {
    t2 = () => {
      initSalesforceInteractions(buildSalesforceConsents(marketingGranted, analyticsGranted))?.then(() => {
        if (pageContextEnabled) {
          sendPageViewSoon();
        } else {
          sendWebsiteEngagementEvent("page-view");
        }
        ziCompany.markSdkReady();
      });
    };
    $[2] = analyticsGranted;
    $[3] = marketingGranted;
    $[4] = ziCompany;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleBeaconLoad = t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (pageContextEnabled) {
        return;
      }
      if (isSalesforceInteractionsReady()) {
        sendWebsiteEngagementEvent("page-view");
      }
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== pathname) {
    t4 = [pathname, pageContextEnabled];
    $[7] = pathname;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      if (isSalesforceInteractionsReady()) {
        sendPageViewSoon();
      }
    };
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleNavigation = t5;
  let t6;
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => () => {
      cancelPendingPageView.current?.();
    };
    t7 = [];
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      const handleClick = _temp;
      window.addEventListener("click", handleClick, pageContextEnabled);
      return () => window.removeEventListener("click", handleClick, pageContextEnabled);
    };
    t9 = [pageContextEnabled];
    $[12] = t8;
    $[13] = t9;
  } else {
    t8 = $[12];
    t9 = $[13];
  }
  useEffect(t8, t9);
  let t10;
  if ($[14] !== marketingGranted) {
    t10 = marketingGranted && gtmId && <Script id="gtm-init" strategy="lazyOnload" dangerouslySetInnerHTML={{
      __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','${gtmId}');`
    }} />;
    $[14] = marketingGranted;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== analyticsGranted) {
    t11 = analyticsGranted && gaId && <GoogleAnalytics gaId={gaId} />;
    $[16] = analyticsGranted;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = pageContextEnabled && c360aBeaconId && <NavigationObserver onNavigate={handleNavigation} />;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== anyGranted || $[20] !== handleBeaconLoad) {
    t13 = anyGranted && c360aBeaconId && <Script src={`https://cdn.c360a.salesforce.com/beacon/c360a/${c360aBeaconId}/scripts/c360a.min.js`} strategy="lazyOnload" onLoad={handleBeaconLoad} />;
    $[19] = anyGranted;
    $[20] = handleBeaconLoad;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t13) {
    t14 = <>{t10}{t11}{t12}{t13}</>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  return t14;
}
function _temp(event) {
  if (!isSalesforceInteractionsReady()) {
    return;
  }
  const target = resolveClickTarget(event.target);
  const name = clickEngagementEventName(target);
  if (name) {
    sendWebsiteEngagementEvent(name, clickTargetPath(target));
  }
}